import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import AuthGuard from "components/Guard/AuthGuard";
import PermissionGuard from "components/Guard/PermissionGuard";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import { AppViewModel } from "services/AppViewModel";
import "animate.css";

const Main = lazy(() => import("pages/Main/Main"));
const Works = lazy(() => import("pages/Works/Works"));
const WorkDetail = lazy(() => import("pages/Works/WorkDetail"));
const WorkRegist = lazy(() => import("pages/Works/WorkRegist"));
const WorkEdit = lazy(() => import("pages/Works/WorkEdit"));
const Resume = lazy(() => import("pages/Resume/Resume"));
const Friends = lazy(() => import("pages/Friends/Friends"));
const Mypage = lazy(() => import("pages/Mypage/Mypage"));
const AdminPage = lazy(() => import("pages/Mypage/AdminPage"));
const Sign = lazy(() => import("pages/Signup/Sign"));
const Login = lazy(() => import("pages/Signup/Login"));
const SearchAuth = lazy(() => import("pages/Signup/SearchAuth"));
const ChangeAuth = lazy(() => import("pages/Signup/ChangeAuth"));
const Error404 = lazy(() => import("pages/Error404/Error404"));

const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      minHeight: "calc(var(--vh, 1vh) * 100)",
      flexDirection: "column",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}
  >
    <div
      style={{
        width: "40px",
        height: "40px",
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #3498db",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "1rem",
      }}
    ></div>
    <div style={{ color: "#666", fontSize: "1rem" }}>로딩 중...</div>
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

function App() {
  AppViewModel();

  // 모바일 호환성을 위한 초기화
  useEffect(() => {
    // 모바일에서 viewport height 문제 해결
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);

    // iOS Safari에서 100vh 문제 해결
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.style.height = "100vh";
      document.body.style.overflow = "hidden";
    }

    // Unhandled errors 처리
    const handleError = (event: ErrorEvent) => {
      console.error("Unhandled error:", event.error);
      console.error("Error message:", event.message);
      console.error("Error filename:", event.filename);
      console.error("Error line:", event.lineno);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("resize", setVH);
      window.removeEventListener("orientationchange", setVH);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Main />} />
          <Route path="/works" element={<Works />} />
          <Route path="/works/:id" element={<WorkDetail />} />
          <Route
            path="/work-regist"
            element={
              <AuthGuard>
                <WorkRegist />
              </AuthGuard>
            }
          />
          <Route
            path="/work-edit/:id"
            element={
              <AuthGuard>
                <WorkEdit />
              </AuthGuard>
            }
          />
          {/* <Route path='/resume' element={<Resume />} />
          <Route
            path='/friends'
            element={
              <AuthGuard>
                <Friends />
              </AuthGuard>
            }
          /> */}
          <Route
            path="/mypage"
            element={
              <AuthGuard>
                <Mypage />
              </AuthGuard>
            }
          />
          <Route
            path="/adminpage"
            element={
              <PermissionGuard>
                <AdminPage />
              </PermissionGuard>
            }
          />
          <Route path="/sign" element={<Sign />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/searchpw" element={<SearchAuth />} />
          <Route path="/login/chagepw" element={<ChangeAuth />} />
          <Route path="/404" element={<Error404 />} />
          <Route path="*" element={<Error404 />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
